import * as i0 from '@angular/core';
import { EventEmitter, Output, Input, Component, NgModule } from '@angular/core';
import * as i1$1 from '@yourcause/common/modals';
import { YCModalComponent, ModalsModule } from '@yourcause/common/modals';
import * as i4 from '@angular/forms';
import { Validators, ReactiveFormsModule } from '@angular/forms';
import * as i1 from '@yourcause/common/core-forms';
import { SiblingValueValidator, MinMaxValidator, RegexValidator, RegexUI } from '@yourcause/common/form-control-validation';
import * as i2 from '@yourcause/common/i18n';
import { I18nModule } from '@yourcause/common/i18n';
import { Subscription } from 'rxjs';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i5 from '@yourcause/common/radio-buttons';
import { RadioButtonsModule } from '@yourcause/common/radio-buttons';
import * as i6 from '@yourcause/common/checkbox';
import { CheckboxModule } from '@yourcause/common/checkbox';
import * as i7 from '@yourcause/common/input';
import { InputModule } from '@yourcause/common/input';
import { RouterModule } from '@angular/router';
import { ButtonModule } from '@yourcause/common/buttons';
import { IconsModule } from '@yourcause/common/icons';

var DownloadType;
(function (DownloadType) {
    DownloadType[DownloadType["CSV"] = 1] = "CSV";
    DownloadType[DownloadType["EXCEL"] = 2] = "EXCEL";
    DownloadType[DownloadType["CSV_ADVANCED"] = 3] = "CSV_ADVANCED";
})(DownloadType || (DownloadType = {}));

class DownloadOptionsComponent {
    constructor(formBuilder, i18n) {
        this.formBuilder = formBuilder;
        this.i18n = i18n;
        this.onValidChange = new EventEmitter();
        this.onChange = new EventEmitter();
        this.DownloadTypes = DownloadType;
        this.downloadTypeOptions = [{
                label: this.i18n.translate('common:textCSV', {}, 'CSV'),
                value: DownloadType.CSV
            }, {
                label: this.i18n.translate('common:textExcel', {}, 'Excel'),
                value: DownloadType.EXCEL
            }, {
                label: this.i18n.translate('common:textCsvAdvanced', {}, 'CSV - Advanced'),
                value: DownloadType.CSV_ADVANCED
            }];
        this.sub = new Subscription();
    }
    ngOnInit() {
        this.formGroup = this.formBuilder.group({
            downloadType: [this.downloadType ?? DownloadType.CSV, Validators.required],
            includeBom: this.includeBom ?? true,
            delimiter: this.delimiter ?? ','
        }, {
            validator: [
                SiblingValueValidator('delimiter', 'downloadType', DownloadType.CSV_ADVANCED, [
                    Validators.required,
                    MinMaxValidator('maxLength', 1, '', this.i18n.translate('common:textCannotBeMoreThanMaxChars', {
                        max: 1
                    }, 'Cannot be more than __max__ characters'), false),
                    RegexValidator(RegexUI.RegexFormattingValues.ASCII, {
                        delimiter: {
                            i18nKey: 'common:textMustBeAnAsciiCharacter',
                            defaultValue: 'Must be an ASCII character'
                        }
                    })
                ])
            ]
        });
        this.sub.add(this.formGroup.valueChanges.subscribe(() => {
            this.emitValidityChange();
        }));
        this.emitValidityChange();
        this.emitChanges();
    }
    emitChanges() {
        const formValue = this.formGroup.value;
        const isAdvanced = formValue.downloadType === DownloadType.CSV_ADVANCED;
        this.onChange.emit({
            downloadType: formValue.downloadType === DownloadType.EXCEL ?
                formValue.downloadType :
                DownloadType.CSV,
            delimiter: isAdvanced ? formValue.delimiter : ',',
            includeBom: isAdvanced ? formValue.includeBom : true
        });
    }
    emitValidityChange() {
        this.onValidChange.emit(!this.formGroup.invalid);
    }
    ngOnDestroy() {
        this.sub.unsubscribe();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: DownloadOptionsComponent, deps: [{ token: i1.TypeSafeFormBuilder }, { token: i2.I18nService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: DownloadOptionsComponent, isStandalone: false, selector: "yc-download-options", inputs: { downloadType: "downloadType", includeBom: "includeBom", delimiter: "delimiter" }, outputs: { onValidChange: "onValidChange", onChange: "onChange" }, ngImport: i0, template: "<div [formGroup]=\"formGroup\">\n  <!-- Download Type -->\n  <div class=\"mb-2\">\n    <yc-radio-buttons\n      name=\"downloadType\"\n      [label]=\"'common:textSelectFormatForDownload' | ycI18n: 'Select the format for download'\"\n      [options]=\"downloadTypeOptions\"\n      (onChange)=\"emitChanges()\">\n    </yc-radio-buttons>\n  </div>\n\n  <!-- CSV Advanced Options -->\n  <div *ngIf=\"formGroup.value.downloadType === DownloadTypes.CSV_ADVANCED\"\n    class=\"ms-4\">\n    <div class=\"mb-2\">\n      <yc-checkbox\n        name=\"includeBom\"\n        [optionLabel]=\"'common:textIncludeBom' | ycI18n: 'Include byte-order mark (BOM)'\"\n        (onChange)=\"emitChanges()\">\n      </yc-checkbox>\n    </div>\n    <div>\n      <yc-input\n        name=\"delimiter\"\n        [label]=\"'common:textDelimiter' | ycI18n: 'Delimiter'\"\n        [help]=\"'common:textDelimiterDesc' | ycI18n: 'The character that separates values in a CSV file'\"\n        (onChange)=\"emitChanges()\">\n      </yc-input>\n    </div>\n  </div>\n</div>", styles: [""], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i4.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i4.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "component", type: i5.RadioButtonsComponent, selector: "yc-radio-buttons", inputs: ["horizontal", "forceWidth", "noMargin", "minWidthForOptions", "radioSpacing", "display", "useVirtualScroll", "centerVertical", "doNotApplyColClass"] }, { kind: "component", type: i6.CheckboxComponent, selector: "yc-checkbox", inputs: ["optionLabel", "optionLabelSrOnly", "noMargin", "indentOption", "centerLabel"], outputs: ["onClick"] }, { kind: "component", type: i7.InputComponent, selector: "yc-input", inputs: ["isNumber", "currencyData", "numberOfDecimals", "requireDecimal", "inputClass", "rounded", "noBorderRadius", "autofocus", "isLocked", "placeholder", "popoverText", "danger", "maxLength", "min", "max", "prefix", "suffix", "prefixSuffixFlexBasis", "showWordCount", "showCharCount", "disablePaste", "customMaskPattern", "customRegexPattern"] }, { kind: "pipe", type: i2.I18nPipe, name: "ycI18n" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: DownloadOptionsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'yc-download-options', standalone: false, template: "<div [formGroup]=\"formGroup\">\n  <!-- Download Type -->\n  <div class=\"mb-2\">\n    <yc-radio-buttons\n      name=\"downloadType\"\n      [label]=\"'common:textSelectFormatForDownload' | ycI18n: 'Select the format for download'\"\n      [options]=\"downloadTypeOptions\"\n      (onChange)=\"emitChanges()\">\n    </yc-radio-buttons>\n  </div>\n\n  <!-- CSV Advanced Options -->\n  <div *ngIf=\"formGroup.value.downloadType === DownloadTypes.CSV_ADVANCED\"\n    class=\"ms-4\">\n    <div class=\"mb-2\">\n      <yc-checkbox\n        name=\"includeBom\"\n        [optionLabel]=\"'common:textIncludeBom' | ycI18n: 'Include byte-order mark (BOM)'\"\n        (onChange)=\"emitChanges()\">\n      </yc-checkbox>\n    </div>\n    <div>\n      <yc-input\n        name=\"delimiter\"\n        [label]=\"'common:textDelimiter' | ycI18n: 'Delimiter'\"\n        [help]=\"'common:textDelimiterDesc' | ycI18n: 'The character that separates values in a CSV file'\"\n        (onChange)=\"emitChanges()\">\n      </yc-input>\n    </div>\n  </div>\n</div>" }]
        }], ctorParameters: () => [{ type: i1.TypeSafeFormBuilder }, { type: i2.I18nService }], propDecorators: { downloadType: [{
                type: Input
            }], includeBom: [{
                type: Input
            }], delimiter: [{
                type: Input
            }], onValidChange: [{
                type: Output
            }], onChange: [{
                type: Output
            }] } });

class DownloadOptionsModalComponent extends YCModalComponent {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: DownloadOptionsModalComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: DownloadOptionsModalComponent, isStandalone: false, selector: "yc-download-options-modal", inputs: { modalHeader: "modalHeader", modalSubHeader: "modalSubHeader" }, usesInheritance: true, ngImport: i0, template: "<yc-modal\n  [modalHeader]=\"modalHeader\"\n  [modalSubHeader]=\"modalSubHeader\"\n  [primaryDisabled]=\"!isValid\"\n  [primaryButtonText]=\"'common:textDownload' | ycI18n: 'Download'\"\n  [cancelButtonText]=\"'common:btnCancel' | ycI18n: 'Cancel'\"\n  (onCancel)=\"closeModal.emit()\"\n  (onPrimaryClick)=\"closeModal.emit(downloadOptions)\">\n\n  <yc-download-options\n    (onChange)=\"downloadOptions = $event\"\n    (onValidChange)=\"isValid = $event\">\n  </yc-download-options>\n\n</yc-modal>", styles: [""], dependencies: [{ kind: "component", type: i1$1.ModalContentComponent, selector: "yc-modal", inputs: ["modalHeader", "hideFooter", "primaryButtonText", "modalSubHeader", "secondaryButtonText", "cancelButtonText", "centerActionButtons", "customHeaderFontColor", "primaryCustomColor", "secondaryCustomColor", "primaryDisabled", "secondaryDisabled", "secondaryIsButton", "processing", "simple", "tertiaryButtonText", "tertiaryDisabled", "quaternaryButtonText", "quaternaryDisabled", "leftAlignedLinkText", "leftAlignedLinkDisabled", "formGroup", "useForm", "helpLink", "helpLinkIconCustomColor", "primaryButtonId"], outputs: ["onPrimaryClick", "onSecondaryClick", "onCancel", "onLeftAlignedLinkClick", "onTertiaryClick", "onQuaternaryClick"] }, { kind: "component", type: DownloadOptionsComponent, selector: "yc-download-options", inputs: ["downloadType", "includeBom", "delimiter"], outputs: ["onValidChange", "onChange"] }, { kind: "pipe", type: i2.I18nPipe, name: "ycI18n" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: DownloadOptionsModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'yc-download-options-modal', standalone: false, template: "<yc-modal\n  [modalHeader]=\"modalHeader\"\n  [modalSubHeader]=\"modalSubHeader\"\n  [primaryDisabled]=\"!isValid\"\n  [primaryButtonText]=\"'common:textDownload' | ycI18n: 'Download'\"\n  [cancelButtonText]=\"'common:btnCancel' | ycI18n: 'Cancel'\"\n  (onCancel)=\"closeModal.emit()\"\n  (onPrimaryClick)=\"closeModal.emit(downloadOptions)\">\n\n  <yc-download-options\n    (onChange)=\"downloadOptions = $event\"\n    (onValidChange)=\"isValid = $event\">\n  </yc-download-options>\n\n</yc-modal>" }]
        }], propDecorators: { modalHeader: [{
                type: Input
            }], modalSubHeader: [{
                type: Input
            }] } });

class DownloadOptionsModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: DownloadOptionsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: DownloadOptionsModule, declarations: [DownloadOptionsModalComponent,
            DownloadOptionsComponent], imports: [CommonModule,
            ButtonModule,
            IconsModule,
            RouterModule,
            ReactiveFormsModule,
            RadioButtonsModule,
            I18nModule,
            CheckboxModule,
            InputModule,
            ModalsModule], exports: [DownloadOptionsModalComponent,
            DownloadOptionsComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: DownloadOptionsModule, imports: [CommonModule,
            ButtonModule,
            IconsModule,
            RouterModule,
            ReactiveFormsModule,
            RadioButtonsModule,
            I18nModule,
            CheckboxModule,
            InputModule,
            ModalsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: DownloadOptionsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        ButtonModule,
                        IconsModule,
                        RouterModule,
                        ReactiveFormsModule,
                        RadioButtonsModule,
                        I18nModule,
                        CheckboxModule,
                        InputModule,
                        ModalsModule
                    ],
                    declarations: [
                        DownloadOptionsModalComponent,
                        DownloadOptionsComponent
                    ],
                    exports: [
                        DownloadOptionsModalComponent,
                        DownloadOptionsComponent
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { DownloadOptionsComponent, DownloadOptionsModalComponent, DownloadOptionsModule, DownloadType };
